import { computed, observable, runInAction } from "mobx";
import { bound } from "@frui.ts/helpers";
import type ListViewModel from "./listViewModel";

export default class PagingManagerViewModel<TListViewModel extends ListViewModel<any, any, any> = ListViewModel<any, any, any>> {
  @observable itemId: string;

  @computed
  get items() {
    return this.listViewModel.items;
  }

  @computed
  get currentIndex(): number {
    return this.items.findIndex(item => item.id === this.itemId);
  }

  @computed
  get currentPaging() {
    return this.listViewModel.currentPaging;
  }

  @computed
  get hasPrevious() {
    if (this.currentPaging.offset > 0) {
      return true;
    }

    return this.currentIndex - 1 >= 0;
  }

  @computed
  get hasNext() {
    if (this.currentPaging.limit + this.currentPaging.offset < this.currentPaging.totalItems) {
      return true;
    }

    return this.currentIndex + 1 <= this.items.length - 1;
  }

  constructor(private listViewModel: TListViewModel, itemId: string) {
    this.itemId = itemId;
  }

  @bound
  async onPreviousClick() {
    if (!this.hasPrevious) {
      return undefined;
    }
    const previousIndex = this.currentIndex - 1;

    if (previousIndex >= 0) {
      this.itemId = this.items[previousIndex].id;
    } else {
      runInAction(() => (this.listViewModel.pagingFilter.offset -= this.listViewModel.pagingFilter.limit));
      await this.listViewModel.loadData();
      this.itemId = this.items[this.items.length - 1].id;
    }
    return this.itemId;
  }

  @bound
  async onNextClick() {
    if (!this.hasNext) {
      return undefined;
    }

    const nextIndex = this.currentIndex + 1;

    if (nextIndex < this.items.length) {
      this.itemId = this.items[nextIndex].id;
    } else {
      runInAction(() => (this.listViewModel.pagingFilter.offset += this.listViewModel.pagingFilter.limit));
      await this.listViewModel.loadData();
      this.itemId = this.items[0].id;
    }

    return this.itemId;
  }

  static Factory<TListViewModel extends ListViewModel<any, any, any, any>>() {
    return (listViewModel: TListViewModel, itemId: string) => new PagingManagerViewModel(listViewModel, itemId);
  }
}
